/**
 * Translates a connector target outcome into the setup-revision failure it
 * means, so no caller has to decide for itself what a failure was.
 */

import type { SetupRevisionFailureKind } from '../setupRevision/revisionFailure';
import type { ConnectorTargetBlockReason } from './connectorTargetCheckpoint';
import type { ConnectorTargetOutcome, ConnectorTargetPhase } from './connectorTargetTransaction';

/** A retryable stop: the phase that failed says which step to name. */
const FailureByPhase: Record<ConnectorTargetPhase, SetupRevisionFailureKind> = {
  undo: 'undoFailed',
  identity: 'documentChanged',
  apply: 'applyFailed',
};

/**
 * The block reasons left out here are preconditions the Connectors step states
 * itself; they stop the revision before it changes anything, so they name no
 * revision failure.
 */
const FailureByBlockReason: Partial<Record<ConnectorTargetBlockReason, SetupRevisionFailureKind>> = {
  'document-changed': 'documentChanged',
  'checkpoint-not-undoable': 'undoFailed',
};

export const connectorTargetRevisionFailure = (
  outcome: ConnectorTargetOutcome
): SetupRevisionFailureKind | null => {
  switch (outcome.kind) {
    case 'applied':
    case 'unchanged':
      return null;
    case 'blocked':
      return FailureByBlockReason[outcome.reason] ?? null;
    case 'failed':
      return FailureByPhase[outcome.phase];
    // The model was left mid-replacement whichever phase stopped it.
    case 'recovery-required':
      return 'applyFailedRecoveryRequired';
    default:
      return null;
  }
};
